import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Breadcrumbs,
  Link,
  Chip,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

const TermsOfServicePage: React.FC = () => {
  const { t } = useTranslation();

  const sections = [
    {
      title: 'Acceptance of Terms',
      content: [
        'By accessing and using GamiFly Tech\'s website and services, you accept and agree to be bound by these Terms of Service.',
        'If you do not agree to these terms, please do not use our website or services.',
        'These terms constitute a legally binding agreement between you and GamiFly Tech.',
        'We reserve the right to modify these terms at any time with notice.',
      ]
    },
    {
      title: 'Description of Services',
      content: [
        'Software Development: Custom application development, web development, and mobile app development.',
        'Consulting Services: Technology consulting, system architecture, and digital transformation guidance.',
        'Maintenance & Support: Ongoing technical support, bug fixes, and system maintenance.',
        'Project Management: End-to-end project management for technology initiatives.',
        'Training Services: Technical training and knowledge transfer sessions.',
      ]
    },
    {
      title: 'User Responsibilities',
      content: [
        'Provide accurate and complete information when requesting services.',
        'Cooperate with our team and provide necessary access and resources.',
        'Make timely payments according to agreed terms.',
        'Respect intellectual property rights and confidentiality agreements.',
        'Use our services in compliance with applicable laws and regulations.',
        'Maintain the security of any access credentials provided.',
      ]
    },
    {
      title: 'Payment Terms',
      content: [
        'Project fees are determined based on scope, complexity, and timeline.',
        'Payment schedules will be outlined in individual service agreements.',
        'Late payments may incur additional fees as specified in contracts.',
        'All fees are non-refundable unless otherwise specified.',
        'Disputed charges must be reported within 30 days of billing.',
        'We accept various payment methods as outlined in service agreements.',
      ]
    },
    {
      title: 'Intellectual Property Rights',
      content: [
        'Client Data: Clients retain ownership of their data and proprietary information.',
        'Custom Development: Ownership of custom-developed software is typically transferred upon full payment.',
        'Pre-existing IP: GamiFly Tech retains rights to pre-existing intellectual property and tools.',
        'Third-party Components: Some solutions may incorporate third-party components with separate licenses.',
        'Documentation: Technical documentation created during projects may be retained for reference.',
      ]
    },
    {
      title: 'Confidentiality',
      content: [
        'We maintain strict confidentiality regarding client information and projects.',
        'Non-disclosure agreements may be executed for sensitive projects.',
        'Client information will not be shared with third parties without consent.',
        'We implement appropriate security measures to protect confidential information.',
        'Confidentiality obligations survive termination of service agreements.',
      ]
    },
    {
      title: 'Warranties and Disclaimers',
      content: [
        'We provide services with professional skill and care.',
        'Software is provided "as is" unless otherwise specified in warranties.',
        'We do not warrant that software will be error-free or operate without interruption.',
        'Third-party components may have separate warranty terms.',
        'Force majeure events may affect service delivery timelines.',
      ]
    },
    {
      title: 'Limitation of Liability',
      content: [
        'Our liability is limited to the amount paid for the specific service.',
        'We are not liable for indirect, incidental, or consequential damages.',
        'Clients are responsible for backing up their data.',
        'Time limitations apply to claims regarding our services.',
        'Some jurisdictions may not allow limitation of liability.',
      ]
    },
    {
      title: 'Service Level Agreements',
      content: [
        'Response times and availability targets are specified in individual agreements.',
        'Maintenance windows may be scheduled for system updates.',
        'Emergency support availability depends on service level purchased.',
        'Performance metrics and reporting may be included in enterprise agreements.',
      ]
    },
    {
      title: 'Termination',
      content: [
        'Either party may terminate agreements with appropriate notice.',
        'Immediate termination may occur for breach of terms or non-payment.',
        'Upon termination, clients retain access to deliverables paid for.',
        'Ongoing obligations regarding confidentiality and IP continue post-termination.',
        'Final invoices and deliverables will be processed according to agreement terms.',
      ]
    },
    {
      title: 'Dispute Resolution',
      content: [
        'We encourage resolution of disputes through direct communication.',
        'Mediation may be pursued before formal legal proceedings.',
        'Applicable law and jurisdiction are specified in individual agreements.',
        'Class action waivers may apply where legally permissible.',
      ]
    },
    {
      title: 'General Provisions',
      content: [
        'These terms are governed by applicable law in our jurisdiction.',
        'If any provision is found invalid, the remaining terms continue in effect.',
        'These terms represent the complete agreement regarding website use.',
        'Individual service agreements may contain additional specific terms.',
        'We may assign our rights and obligations with notice.',
      ]
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header />
      
      <Container maxWidth="lg" sx={{ flex: 1, py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link component={RouterLink} to="/" color="inherit">
            Home
          </Link>
          <Typography color="text.primary">Terms of Service</Typography>
        </Breadcrumbs>

        {/* Header Section */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 4, 
            mb: 4, 
            background: 'linear-gradient(135deg, #5C6BC0 0%, #6C5CE7 100%)',
            color: 'white'
          }}
        >
          <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
            Terms of Service
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, mb: 2 }}>
            GamiFly Tech - Service Agreement Terms
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Chip 
              label="Effective: December 2024" 
              sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
            />
            <Chip 
              label="Version 2.1" 
              sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
            />
          </Box>
        </Paper>

        {/* Important Notice */}
        <Alert 
          severity="info" 
          sx={{ mb: 4, '& .MuiAlert-message': { width: '100%' } }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            Important Notice
          </Typography>
          <Typography variant="body2">
            These Terms of Service apply to our website and general service information. 
            Specific project engagements are governed by separate Service Agreements that may contain additional or modified terms. 
            Please review both documents carefully before engaging our services.
          </Typography>
        </Alert>

        {/* Introduction */}
        <Paper elevation={1} sx={{ p: 4, mb: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}>
            Introduction
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
            Welcome to GamiFly Tech. These Terms of Service ("Terms") govern your use of our website, 
            services, and any related agreements. By using our services, you agree to these terms.
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
            GamiFly Tech provides professional software development, consulting, and technology services. 
            Our commitment is to deliver high-quality solutions while maintaining transparency in our business relationships.
          </Typography>
          <Box sx={{ p: 3, backgroundColor: 'warning.light', borderRadius: 2, color: 'warning.contrastText' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Legal Notice: These terms constitute a binding legal agreement. Please read them carefully and contact us if you have questions.
            </Typography>
          </Box>
        </Paper>

        {/* Main Content Sections */}
        {sections.map((section, index) => (
          <Paper key={index} elevation={1} sx={{ p: 4, mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}>
              {index + 1}. {section.title}
            </Typography>
            <List sx={{ pl: 0 }}>
              {section.content.map((item, itemIndex) => (
                <ListItem key={itemIndex} sx={{ pl: 0, pb: 1 }}>
                  <ListItemText
                    primary={item}
                    sx={{
                      '& .MuiListItemText-primary': {
                        lineHeight: 1.7,
                        fontSize: '1rem',
                      }
                    }}
                  />
                </ListItem>
              ))}
            </List>
            {index < sections.length - 1 && <Divider sx={{ mt: 2 }} />}
          </Paper>
        ))}

        {/* Service Categories */}
        <Paper elevation={2} sx={{ p: 4, mb: 4, backgroundColor: 'grey.50' }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}>
            Our Service Categories
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <Box>
              <Typography variant="h6" sx={{ mb: 2, color: 'secondary.main' }}>
                Development Services
              </Typography>
              <List dense>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemText primary="Web Applications" />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemText primary="Mobile Applications" />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemText primary="Enterprise Software" />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemText primary="API Development" />
                </ListItem>
              </List>
            </Box>
            <Box>
              <Typography variant="h6" sx={{ mb: 2, color: 'secondary.main' }}>
                Consulting Services
              </Typography>
              <List dense>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemText primary="Technology Strategy" />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemText primary="System Architecture" />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemText primary="Digital Transformation" />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemText primary="Security Assessment" />
                </ListItem>
              </List>
            </Box>
          </Box>
        </Paper>

        {/* Contact Section */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 4, 
            mt: 4,
            background: 'linear-gradient(135deg, #00CEC9 0%, #55EFC4 100%)',
            color: 'white'
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
            Legal Questions or Concerns?
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
            If you have questions about these Terms of Service or need clarification about any provisions, 
            our legal team is available to assist you. We believe in transparent communication and are happy to discuss our terms.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>Legal Inquiries</Typography>
              <Typography variant="body2">gamiflytech@gmail.com</Typography>
            </Box>
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>Business Development</Typography>
              <Typography variant="body2">gamiflytech@gmail.com</Typography>
            </Box>
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>Contract Negotiations</Typography>
              <Typography variant="body2">By appointment</Typography>
            </Box>
          </Box>
        </Paper>

        {/* Footer Notice */}
        <Paper elevation={1} sx={{ p: 3, mt: 4, backgroundColor: 'info.light', color: 'info.contrastText' }}>
          <Typography variant="body2" sx={{ textAlign: 'center', fontWeight: 600 }}>
            These Terms of Service were last updated on December 15, 2024. 
            For the most current version, please visit this page regularly or contact us directly.
          </Typography>
        </Paper>
      </Container>

      <Footer />
    </Box>
  );
};

export default TermsOfServicePage; 